import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ConnectorService } from '../connector.service';

export type AnalyticsEventType =
  | 'PAGE_VIEW'
  | 'FEATURE_USE'
  | 'ERROR'
  | 'FLOW_ABANDONED'
  | 'CAMPAIGN_STEP';

export interface AnalyticsEventData {
  entity_type?: string;
  entity_id?: string;
  metadata?: Record<string, any>;
}

interface StoredSession {
  id: string;
  lastSeen: number;
}

/**
 * Collecte d'activité first-party.
 * - Connecté : POST /activity/track avec un session_id (onglet, 30 min d'inactivité).
 * - Non connecté : POST /activity/track/public, sans aucun identifiant.
 * Plateforme et version de l'app sont ajoutées en en-têtes par ClientContextInterceptor.
 * Fire-and-forget : une erreur de tracking ne doit jamais impacter l'utilisateur.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private static readonly SESSION_KEY = 'analytics_session';
  private static readonly SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  private static readonly MAX_PAGE_DURATION_MS = 30 * 60 * 1000;
  private static readonly DUPLICATE_WINDOW_MS = 1000;

  private started = false;
  private landingSource: Record<string, string> | null = null;
  private currentPage: { key: string; route: string; since: number } | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private connector: ConnectorService
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.landingSource = this.captureLandingSource();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.trackPageView());

    if (this.router.navigated) {
      this.trackPageView();
    }
  }

  track(eventType: AnalyticsEventType, data: AnalyticsEventData = {}): void {
    try {
      const connected = this.connector.isConnected();
      const url = environment.backAPI + (connected ? '/activity/track' : '/activity/track/public');
      const body = connected
        ? { event_type: eventType, session_id: this.sessionId(), ...data }
        : { event_type: eventType, ...data };
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (connected) {
        headers['Authorization'] = 'Bearer ' + this.connector.token();
      }

      this.http.post(url, body, { headers })
        .pipe(catchError(() => of(null)))
        .subscribe();
    } catch {
      // Le tracking ne doit jamais casser l'application
    }
  }

  private trackPageView(): void {
    const { route, params } = this.currentRoute();
    const key = route + JSON.stringify(params);
    const now = Date.now();

    // Redirections et doubles NavigationEnd sur la même page
    if (this.currentPage?.key === key && now - this.currentPage.since < AnalyticsService.DUPLICATE_WINDOW_MS) {
      return;
    }

    const metadata: Record<string, any> = {};
    if (Object.keys(params).length) {
      metadata['params'] = params;
    }
    if (this.currentPage) {
      metadata['prev_route'] = this.currentPage.route;
      metadata['prev_duration_ms'] = Math.min(now - this.currentPage.since, AnalyticsService.MAX_PAGE_DURATION_MS);
    }
    if (this.landingSource) {
      // Provenance attachée uniquement à la page d'arrivée
      Object.assign(metadata, { landing: true, ...this.landingSource });
      this.landingSource = null;
    }

    this.currentPage = { key, route, since: now };
    this.track('PAGE_VIEW', { entity_type: 'route', entity_id: route.slice(0, 100), metadata });
  }

  /** Motif de route ('/c/:slug') plutôt que l'URL, pour pouvoir agréger. */
  private currentRoute(): { route: string; params: Record<string, string> } {
    const parts: string[] = [];
    const params: Record<string, string> = {};
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (snapshot) {
      if (snapshot.routeConfig?.path) {
        parts.push(snapshot.routeConfig.path);
      }
      Object.assign(params, snapshot.params);
      snapshot = snapshot.firstChild;
    }
    return { route: '/' + parts.join('/'), params };
  }

  /** utm_* et site référent (hors domaine courant) de la page d'arrivée. */
  private captureLandingSource(): Record<string, string> {
    const source: Record<string, string> = {};
    try {
      const query = new URLSearchParams(window.location.search);
      for (const key of ['utm_source', 'utm_medium', 'utm_campaign']) {
        const value = query.get(key);
        if (value) {
          source[key] = value.slice(0, 100);
        }
      }
      if (document.referrer) {
        const referrerHost = new URL(document.referrer).hostname;
        if (referrerHost && referrerHost !== window.location.hostname) {
          source['referrer_host'] = referrerHost.slice(0, 100);
        }
      }
    } catch {
      // URL ou référent invalide : on ignore
    }
    return source;
  }

  private sessionId(): string {
    const now = Date.now();
    try {
      const raw = sessionStorage.getItem(AnalyticsService.SESSION_KEY);
      const stored: StoredSession | null = raw ? JSON.parse(raw) : null;
      const session: StoredSession = stored && now - stored.lastSeen < AnalyticsService.SESSION_TIMEOUT_MS
        ? { id: stored.id, lastSeen: now }
        : { id: this.uuid(), lastSeen: now };
      sessionStorage.setItem(AnalyticsService.SESSION_KEY, JSON.stringify(session));
      return session.id;
    } catch {
      return this.uuid();
    }
  }

  private uuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
}
